/**
 * Performance Monitor Component
 * Displays Core Web Vitals in development mode
 */

import React, { useState } from 'react';
import { useWebVitals, WebVitalsMetric } from '../hooks/useWebVitals';

export const PerformanceMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<Record<string, WebVitalsMetric>>({});
  const [isVisible, setIsVisible] = useState(process.env.NODE_ENV === 'development');

  useWebVitals({
    onMetric: (metric) => {
      // Only update state in development
      if (process.env.NODE_ENV === 'development') {
        setMetrics(prev => ({
          ...prev,
          [metric.name]: metric
        }));
      }
    },
    // Report to analytics in both dev and prod, but don't render UI in prod
    reportToAnalytics: true
  });

  // Completely skip rendering in production
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'good': return '#0CCE6B';
      case 'needs-improvement': return '#FFA400';
      case 'poor': return '#FF4E42';
      default: return '#999';
    }
  };

  const metricsToDisplay = ['LCP', 'FID', 'CLS', 'INP', 'FCP', 'TTFB'];

  return (
    <div style={{
      position: 'fixed',
      bottom: 10,
      right: 10,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '11px',
      fontFamily: 'monospace',
      zIndex: 9999,
      minWidth: '200px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        paddingBottom: '8px'
      }}>
        <strong>⚡ Core Web Vitals</strong>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '0 4px',
            fontSize: '16px'
          }}
        >
          ×
        </button>
      </div>
      
      {metricsToDisplay.map(name => {
        const metric = metrics[name];
        return (
          <div
            key={name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '4px',
              alignItems: 'center'
            }}
          >
            <span>{name}:</span>
            <span style={{
              fontWeight: 'bold',
              color: metric ? getRatingColor(metric.rating) : '#999'
            }}>
              {metric ? `${Math.round(metric.value)}${name === 'CLS' ? '' : 'ms'}` : '---'}
            </span>
          </div>
        );
      })}

      <div style={{
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        fontSize: '9px',
        color: '#999'
      }}>
        <div>🟢 Good | 🟡 Needs Improvement | 🔴 Poor</div>
      </div>
    </div>
  );
};

export default PerformanceMonitor;


